import { appBackendApiClient } from './apiClient';

export type NotificationCategory =
  | 'DONATION_SUCCESS'
  | 'DONATION_JOURNEY'
  | 'CIRCLE_MILESTONE'
  | 'CHALLENGE_STARTED'
  | 'CHALLENGE_ENDING_SOON'
  | 'STORY_APPROVED'
  | 'PAYMENT_ALERT'
  | 'ACCOUNT_UPDATE'
  | 'ADMIN_BROADCAST';

export type ScheduledNotificationStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'FAILED'
  | 'CANCELLED';

export type NotificationAudienceKind =
  | 'ALL_USERS'
  | 'DONORS'
  | 'CIRCLE_MEMBERS'
  | 'CHALLENGE_PARTICIPANTS'
  | 'INACTIVE_USERS'
  | 'CUSTOM_SEGMENT'
  | 'SPECIFIC_USERS';

export interface ScheduledNotification {
  id: string;
  templateCode: string | null;
  category: NotificationCategory;
  titleEn: string;
  titleAr: string;
  bodyEn: string;
  bodyAr: string;
  deepLink: string | null;
  data: Record<string, unknown>;
  audienceKind: NotificationAudienceKind;
  audienceFilter: Record<string, unknown>;
  scheduledFor: string;
  timezone: string;
  status: ScheduledNotificationStatus;
  createdByAdminId: string;
  totalRecipients: number | null;
  successCount: number;
  failureCount: number;
  startedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduledListResponse {
  items: ScheduledNotification[];
  nextCursor: string | null;
}

export interface PreviewInput {
  titleEn: string;
  titleAr: string;
  bodyEn: string;
  bodyAr: string;
  deepLink?: string;
  variables?: Record<string, unknown>;
}

export interface PreviewResult {
  en: { title: string; body: string; deepLink: string | null };
  ar: { title: string; body: string; deepLink: string | null };
}

export interface CreateInput {
  templateCode?: string;
  category?: NotificationCategory;
  titleEn: string;
  titleAr: string;
  bodyEn: string;
  bodyAr: string;
  deepLink?: string;
  data?: Record<string, unknown>;
  audienceKind: NotificationAudienceKind;
  audienceFilter?: Record<string, unknown>;
  scheduledFor?: string;
  timezone?: string;
  saveAsDraft?: boolean;
  sendNow?: boolean;
}

export interface AudienceEstimate {
  count: number | null;
  reason?: string;
}

export interface BatchAnalytics {
  batchId: string;
  totals: {
    notifications: number;
    sent: number;
    failed: number;
    suppressed: number;
  };
  deliveries: {
    pending: number;
    sent: number;
    delivered: number;
    failed: number;
    unregistered: number;
  };
  events: {
    delivered: number;
    opened: number;
    clicked: number;
    dismissed: number;
  };
  openRate: number | null;
  clickThroughRate: number | null;
}

export interface GlobalAnalytics {
  window: { from: string | null; to: string | null };
  totals: { sent: number; failed: number; suppressed: number };
  events: { opened: number; clicked: number };
  openRate: number | null;
  clickThroughRate: number | null;
  byCategory: Array<{
    category: NotificationCategory;
    sent: number;
    opened: number;
    clicked: number;
  }>;
}

const notificationsService = {
  async listScheduled(params?: {
    status?: ScheduledNotificationStatus;
    limit?: number;
    cursor?: string;
  }): Promise<ScheduledListResponse> {
    const { data } = await appBackendApiClient.get('/admin/notifications/scheduled', {
      params,
    });
    return data;
  },

  async createScheduled(input: CreateInput): Promise<ScheduledNotification> {
    const { data } = await appBackendApiClient.post(
      '/admin/notifications/scheduled',
      input,
    );
    return data;
  },

  async getScheduled(id: string): Promise<ScheduledNotification> {
    const { data } = await appBackendApiClient.get(
      `/admin/notifications/scheduled/${id}`,
    );
    return data;
  },

  async cancelScheduled(id: string): Promise<{ cancelled: boolean }> {
    const { data } = await appBackendApiClient.post(
      `/admin/notifications/scheduled/${id}/cancel`,
    );
    return data;
  },

  async preview(input: PreviewInput): Promise<PreviewResult> {
    const { data } = await appBackendApiClient.post(
      '/admin/notifications/preview',
      input,
    );
    return data;
  },

  async estimateAudience(
    audienceKind: NotificationAudienceKind,
    audienceFilter?: Record<string, unknown>,
  ): Promise<AudienceEstimate> {
    const { data } = await appBackendApiClient.post(
      '/admin/notifications/audience-estimate',
      { audienceKind, audienceFilter },
    );
    return data;
  },

  async batchAnalytics(id: string): Promise<BatchAnalytics> {
    const { data } = await appBackendApiClient.get(
      `/admin/notifications/scheduled/${id}/analytics`,
    );
    return data;
  },

  async globalAnalytics(params?: {
    from?: string;
    to?: string;
    category?: NotificationCategory;
  }): Promise<GlobalAnalytics> {
    const { data } = await appBackendApiClient.get(
      '/admin/notifications/analytics/global',
      { params },
    );
    return data;
  },
};

export default notificationsService;
